import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Typography Components
 *
 * Semantic text components following the design system type scale.
 * Use these instead of raw HTML elements for consistent typography.
 */

/* -----------------------------------------------------------------------------
 * Heading Component
 * -------------------------------------------------------------------------- */

const headingVariants = cva("font-serif tracking-tight text-foreground", {
  variants: {
    level: {
      1: "text-4xl font-bold leading-tight md:text-5xl lg:text-6xl",
      2: "text-3xl font-semibold leading-tight md:text-4xl",
      3: "text-2xl font-semibold leading-snug md:text-3xl",
      4: "text-xl font-semibold leading-snug md:text-2xl",
    },
    variant: {
      default: "",
      muted: "text-muted-foreground",
      accent: "text-primary",
    },
  },
  defaultVariants: {
    level: 1,
    variant: "default",
  },
});

type HeadingLevel = 1 | 2 | 3 | 4;

interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  level?: HeadingLevel;
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = 1, variant, ...props }, ref) => {
    const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4";
    return (
      <Tag
        ref={ref}
        className={cn(headingVariants({ level, variant, className }))}
        {...props}
      />
    );
  }
);
Heading.displayName = "Heading";

/* -----------------------------------------------------------------------------
 * Body Component
 * -------------------------------------------------------------------------- */

const bodyVariants = cva("font-sans leading-relaxed", {
  variants: {
    size: {
      lg: "text-lg",
      default: "text-base",
      sm: "text-sm",
    },
    variant: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      accent: "text-primary",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    size: "default",
    variant: "default",
    weight: "normal",
  },
});

interface BodyProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof bodyVariants> {}

const Body = React.forwardRef<HTMLParagraphElement, BodyProps>(
  ({ className, size, variant, weight, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(bodyVariants({ size, variant, weight, className }))}
        {...props}
      />
    );
  }
);
Body.displayName = "Body";

/* -----------------------------------------------------------------------------
 * Caption Component
 * -------------------------------------------------------------------------- */

const captionVariants = cva("font-sans text-xs leading-normal", {
  variants: {
    variant: {
      default: "text-muted-foreground",
      subtle: "text-muted-foreground/70",
      accent: "text-primary",
    },
    uppercase: {
      true: "uppercase tracking-wider font-semibold",
      false: "",
    },
  },
  defaultVariants: {
    variant: "default",
    uppercase: false,
  },
});

interface CaptionProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof captionVariants> {}

const Caption = React.forwardRef<HTMLSpanElement, CaptionProps>(
  ({ className, variant, uppercase, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(captionVariants({ variant, uppercase, className }))}
        {...props}
      />
    );
  }
);
Caption.displayName = "Caption";

/* -----------------------------------------------------------------------------
 * Kicker Component (for section labels)
 * -------------------------------------------------------------------------- */

const kickerVariants = cva(
  "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 font-sans text-kicker text-[0.72rem]",
  {
    variants: {
      variant: {
        default:
          "border border-[var(--surface-border)] bg-[var(--surface-2)] text-muted-foreground",
        accent:
          "border border-accent/20 bg-accent/10 text-accent-foreground",
        primary: "border border-primary/20 bg-primary/10 text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface KickerProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof kickerVariants> {
  icon?: React.ReactNode;
}

const Kicker = React.forwardRef<HTMLSpanElement, KickerProps>(
  ({ className, variant, icon, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(kickerVariants({ variant, className }))}
        {...props}
      >
        {icon}
        {children}
      </span>
    );
  }
);
Kicker.displayName = "Kicker";

/* -----------------------------------------------------------------------------
 * TextLabel Component (for form labels with all-caps styling)
 * -------------------------------------------------------------------------- */

const textLabelVariants = cva(
  "font-sans text-kicker text-xs font-semibold tracking-[0.14em]",
  {
    variants: {
      variant: {
        default: "text-muted-foreground",
        accent: "text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface TextLabelProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof textLabelVariants> {}

const TextLabel = React.forwardRef<HTMLSpanElement, TextLabelProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(textLabelVariants({ variant, className }))}
        {...props}
      />
    );
  }
);
TextLabel.displayName = "TextLabel";

export {
  Heading,
  headingVariants,
  Body,
  bodyVariants,
  Caption,
  captionVariants,
  Kicker,
  kickerVariants,
  TextLabel,
  textLabelVariants,
};
